import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { ProgressSpinner } from 'primereact/progressspinner'
import { textOrDash } from '../../../components/summary/summaryFormat'
import { HospitalizationBedDTO, HospitalizationFilterDTO } from '../../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import { getHospitalizationBeds } from '../../../services/HospitalizationSummaryController'
import useSectionData from '../../UrgencySummary/useSectionData'
import { filtersKey } from '../hospitalizationFilterModel'
import { formatCount, formatPercent } from '../hospitalizationSummaryFormat'

interface HospitalizationSectorBedsProps {
  filters: HospitalizationFilterDTO
  sectorId: number
  sectorName: string
}

/**
 * Las camas de un sector con su ocupación en el período. Se piden recién al expandir el sector (no viajan con el resumen) y
 * se cancelan si el sector se cierra antes de que lleguen; con error, una nota y «Reintentar» sin tapar el resto de la tabla.
 */
export default function HospitalizationSectorBeds({ filters, sectorId, sectorName }: HospitalizationSectorBedsProps) {
  const intl = useIntl()
  const beds = useSectionData(
    `${filtersKey(filters)}|beds|${sectorId}`,
    (signal) => getHospitalizationBeds(filters, sectorId, signal),
  )

  if (beds.status === 'loading') {
    return (
      <span className="nv-loading nv-hosp-beds__state" role="status">
        <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
        <span>{intl.formatMessage({ id: 'HospitalizationSummaryLoading' })}</span>
      </span>
    )
  }
  if (beds.status === 'failed' || beds.data === null) {
    return (
      <div className="nv-hosp-failed nv-hosp-beds__state" role="status">
        <p className="nv-note">
          <i className="pi pi-info-circle" aria-hidden="true" />
          {intl.formatMessage({ id: 'HospitalizationSummaryBedsError' })}
        </p>
        <Button type="button" link label={intl.formatMessage({ id: 'HospitalizationSummaryRetry' })} onClick={beds.retry} />
      </div>
    )
  }
  if (beds.data.length === 0) {
    return <p className="nv-urg-empty">{intl.formatMessage({ id: 'HospitalizationSummaryBedsEmpty' })}</p>
  }

  return (
    <div className="nv-load-table nv-hosp-beds" role="group" aria-label={intl.formatMessage({ id: 'HospitalizationSummaryBedsOf' }, { name: sectorName })}>
      <DataTable value={beds.data} dataKey="bedId">
        <Column header={intl.formatMessage({ id: 'HospitalizationSummaryColRoom' })} body={(bed: HospitalizationBedDTO) => textOrDash(bed.roomNumber)} />
        <Column header={intl.formatMessage({ id: 'HospitalizationSummaryColBed' })} body={(bed: HospitalizationBedDTO) => textOrDash(bed.bedNumber)} />
        <Column
          header={intl.formatMessage({ id: 'HospitalizationSummaryColDaysOccupied' })}
          className="nv-urg-center"
          headerClassName="nv-urg-center"
          body={(bed: HospitalizationBedDTO) => formatCount(intl, bed.patientDays)}
        />
        <Column
          header={intl.formatMessage({ id: 'HospitalizationSummaryColDaysAvailable' })}
          className="nv-urg-center"
          headerClassName="nv-urg-center"
          body={(bed: HospitalizationBedDTO) => formatCount(intl, bed.bedDays)}
        />
        <Column
          header={intl.formatMessage({ id: 'HospitalizationSummaryKpiOccupancy' })}
          className="nv-urg-center"
          headerClassName="nv-urg-center"
          body={(bed: HospitalizationBedDTO) => formatPercent(intl, bed.occupancyPct)}
        />
      </DataTable>
    </div>
  )
}
